import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAdminAuth } from "@/context/Admin"
import { Loader2, Save } from "lucide-react"
import { toast } from "sonner"

export default function LegalPagesManagement() {
  const { getLegalPage, updateLegalPage } = useAdminAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState("privacy")
  const [formData, setFormData] = useState({
    privacy: {},
    terms: {},
    refund: {},
  })

  useEffect(() => {
    const fetchAllPages = async () => {
      setLoading(true)
      try {
        const [privacy, terms, refund] = await Promise.all([
          getLegalPage("privacy").catch(() => null),
          getLegalPage("terms").catch(() => null),
          getLegalPage("refund").catch(() => null),
        ])
        setFormData({
          privacy: privacy || {},
          terms: terms || {},
          refund: refund || {},
        })
      } catch (error) {
        console.error("Error fetching legal pages:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchAllPages()
  }, [getLegalPage])

  const handleSave = async (pageType) => {
    setSaving(true)
    try {
      await updateLegalPage(pageType, formData[pageType])
      toast.success(`${pageType.charAt(0).toUpperCase() + pageType.slice(1)} page updated successfully!`)
    } catch (error) {
      console.error("Error updating legal page:", error)
    } finally {
      setSaving(false)
    }
  }

  const updateField = (pageType, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [pageType]: {
        ...prev[pageType],
        [field]: value,
      },
    }))
  }

  const updateSection = (pageType, sectionIndex, field, value) => {
    setFormData((prev) => {
      const sections = [...(prev[pageType].sections || [])]
      sections[sectionIndex] = { ...sections[sectionIndex], [field]: value }
      return {
        ...prev,
        [pageType]: { ...prev[pageType], sections },
      }
    })
  }

  const updateSectionContent = (pageType, sectionIndex, contentIndex, value) => {
    setFormData((prev) => {
      const sections = [...(prev[pageType].sections || [])]
      const content = [...(sections[sectionIndex].content || [])]
      content[contentIndex] = value
      sections[sectionIndex] = { ...sections[sectionIndex], content }
      return {
        ...prev,
        [pageType]: { ...prev[pageType], sections },
      }
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const renderPageEditor = (pageType, pageTitle) => {
    const page = formData[pageType] || {}

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{pageTitle}</h2>
            <p className="text-gray-600 mt-1">Manage {pageTitle} content</p>
          </div>
          <Button onClick={() => handleSave(pageType)} disabled={saving} className="gap-2">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Hero Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Hero Title</Label>
              <Input
                value={page.hero_title || ""}
                onChange={(e) => updateField(pageType, "hero_title", e.target.value)}
              />
            </div>
            <div>
              <Label>Hero Subtitle</Label>
              <Textarea
                value={page.hero_subtitle || ""}
                onChange={(e) => updateField(pageType, "hero_subtitle", e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label>Badge Text</Label>
              <Input
                value={page.hero_badge_text || ""}
                onChange={(e) => updateField(pageType, "hero_badge_text", e.target.value)}
              />
            </div>
            <div>
              <Label>Last Updated (YYYY-MM-DD)</Label>
              <Input
                type="date"
                value={page.last_updated || ""}
                onChange={(e) => updateField(pageType, "last_updated", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Introduction</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Introduction Title</Label>
              <Input
                value={page.introduction_title || ""}
                onChange={(e) => updateField(pageType, "introduction_title", e.target.value)}
              />
            </div>
            <div>
              <Label>Introduction Content</Label>
              <Textarea
                value={page.introduction_content || ""}
                onChange={(e) => updateField(pageType, "introduction_content", e.target.value)}
                rows={5}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Contact Title</Label>
              <Input
                value={page.contact_title || ""}
                onChange={(e) => updateField(pageType, "contact_title", e.target.value)}
              />
            </div>
            <div>
              <Label>Contact Subtitle</Label>
              <Textarea
                value={page.contact_subtitle || ""}
                onChange={(e) => updateField(pageType, "contact_subtitle", e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                value={page.contact_email || ""}
                onChange={(e) => updateField(pageType, "contact_email", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sections</CardTitle>
            <CardDescription>
              Note: Sections are stored as JSON. For complex editing, use the API directly or edit the database.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={JSON.stringify(page.sections || [], null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value)
                  updateField(pageType, "sections", parsed)
                } catch {
                  // Invalid JSON, keep as is
                }
              }}
              rows={10}
              className="font-mono text-sm"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Content</CardTitle>
            <CardDescription>Additional content like rights, special cases, etc.</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={JSON.stringify(page.additional_content || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value)
                  updateField(pageType, "additional_content", parsed)
                } catch {
                  // Invalid JSON, keep as is
                }
              }}
              rows={10}
              className="font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Legal Pages Management</h1>
        <p className="text-gray-600 mt-1">Manage privacy policy, terms of service, and refund policy</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="privacy">Privacy Policy</TabsTrigger>
          <TabsTrigger value="terms">Terms of Service</TabsTrigger>
          <TabsTrigger value="refund">Refund Policy</TabsTrigger>
        </TabsList>
        <TabsContent value="privacy">{renderPageEditor("privacy", "Privacy Policy")}</TabsContent>
        <TabsContent value="terms">{renderPageEditor("terms", "Terms of Service")}</TabsContent>
        <TabsContent value="refund">{renderPageEditor("refund", "Refund Policy")}</TabsContent>
      </Tabs>
    </div>
  )
}

